import { Deps } from './fp';

const EXTERNAL_REFERENCE_PATTERN =
  /url\(\s*(?!['"]?#)[^)]+\)|@import|(?:https?:)?\/\/|data:|javascript:|vbscript:/i;
const CSS_ESCAPED_NEWLINE_PATTERN = /\\(?:\r\n|[\n\r\f])/g;
const CSS_ESCAPE_PATTERN = /\\(?:([0-9a-fA-F]{1,6})[\t\n\f\r ]?|([^\n\r\f0-9a-fA-F]))/g;
const SAFE_REFERENCE_PATTERN = /^#[A-Za-z][\w:.-]*$/;
const UNSAFE_SERIALIZED_SVG_PATTERN =
  /<(?:[a-z][\w.-]*:)?(?:script|style|foreignObject)\b|\s(?:[a-z][\w.-]*:)?on[a-z]+\s*=|(?:https?:)?\/\/|@import|url\(\s*(?!['"]?#)|(?:data|javascript|vbscript):/i;
const UNSAFE_SVG_ERROR_MESSAGE = 'Uploaded SVG contains unsafe active content';
const SVG_PARSE_ERROR_MESSAGE = 'Uploaded SVG must contain a root svg element';
const SAFE_NAMESPACE_URIS = new Set([
  'http://www.w3.org/2000/svg',
  'http://www.w3.org/1999/xlink',
  'http://www.w3.org/XML/1998/namespace',
  'http://www.w3.org/2000/xmlns/',
]);
const BLOCKED_ACTIVE_ELEMENT_NAMES = new Set([
  'script',
  'style',
  'foreignobject',
  'iframe',
  'object',
  'embed',
  'audio',
  'video',
  'canvas',
  'metadata',
]);

export type SvgSanitizerAttribute = {
  name: string;
  value: unknown;
};

export type SvgSanitizerDocument<ElementRef = unknown> = {
  root?: ElementRef | null;
  hasParseError?(): boolean;
  getElements(): Iterable<ElementRef>;
  getElementName(element: ElementRef): string;
  getAttributes(element: ElementRef): Iterable<SvgSanitizerAttribute>;
  removeElement(element: ElementRef): void;
  removeAttribute(element: ElementRef, attributeName: string): void;
  serialize(root: ElementRef): string;
};

export type SvgSanitizerDeps<ElementRef = unknown> = {
  parseSvg(svg: string): SvgSanitizerDocument<ElementRef>;
  unsafeSvgErrorMessage?: string;
  svgParseErrorMessage?: string;
  externalReferencePattern?: RegExp;
  unsafeSerializedContentPattern?: RegExp;
  safeReferencePattern?: RegExp;
  safeNamespaceUris?: ReadonlySet<string>;
  blockedActiveElementNames?: ReadonlySet<string>;
};

type ParsedSvgDocument<ElementRef> = {
  source: string;
  document: SvgSanitizerDocument<ElementRef>;
  root: ElementRef;
};

export const sanitizeSvg = Deps.pipe(
  parseSvgDocument,
  sanitizeSvgDocument,
  serializeSvgDocument,
  validateSerializedSvg,
);

export function parseSvgDocument<ElementRef>(
  source: string,
  deps: SvgSanitizerDeps<ElementRef>,
): ParsedSvgDocument<ElementRef> {
  const document = deps.parseSvg(source);
  const root = document.root;
  if (
    document.hasParseError?.() ||
    !root ||
    getSvgLocalName(document.getElementName(root)) !== 'svg'
  ) {
    throw new Error(deps.svgParseErrorMessage ?? SVG_PARSE_ERROR_MESSAGE);
  }

  return { source, document, root };
}

export function assertSvgDocumentContentSafe<ElementRef>(
  parsed: ParsedSvgDocument<ElementRef>,
  deps: SvgSanitizerDeps<ElementRef>,
) {
  for (const element of parsed.document.getElements()) {
    assertSafeSvgElementName(parsed.document.getElementName(element), deps);

    for (const attribute of parsed.document.getAttributes(element)) {
      assertSafeSvgAttribute(attribute.name, attribute.value, deps);
    }
  }

  assertSafeSvgSerializedContent(parsed.source, deps);
  return parsed;
}

export function sanitizeSvgDocument<ElementRef>(
  parsed: ParsedSvgDocument<ElementRef>,
  deps: SvgSanitizerDeps<ElementRef>,
) {
  for (const element of parsed.document.getElements()) {
    if (isUnsafeSvgElementName(parsed.document.getElementName(element), deps)) {
      parsed.document.removeElement(element);
      continue;
    }

    for (const attribute of parsed.document.getAttributes(element)) {
      if (isUnsafeSvgAttribute(attribute.name, attribute.value, deps)) {
        parsed.document.removeAttribute(element, attribute.name);
      }
    }
  }

  return parsed;
}

export function serializeSvgDocument<ElementRef>(parsed: ParsedSvgDocument<ElementRef>) {
  return parsed.document.serialize(parsed.root);
}

export function validateSerializedSvg<ElementRef>(svg: string, deps: SvgSanitizerDeps<ElementRef>) {
  const parsed = parseSvgDocument(svg, deps);
  assertSvgDocumentContentSafe(parsed, deps);
  return svg;
}

export function assertSafeSvgElementName(name: string, deps: Partial<SvgSanitizerDeps> = {}) {
  if (isUnsafeSvgElementName(name, deps)) {
    throw new Error(deps.unsafeSvgErrorMessage ?? UNSAFE_SVG_ERROR_MESSAGE);
  }
}

export function assertSafeSvgAttribute(
  name: string,
  value: unknown,
  deps: Partial<SvgSanitizerDeps> = {},
) {
  if (isUnsafeSvgAttribute(name, value, deps)) {
    throw new Error(deps.unsafeSvgErrorMessage ?? UNSAFE_SVG_ERROR_MESSAGE);
  }
}

export function assertSafeSvgSerializedContent(svg: string, deps: Partial<SvgSanitizerDeps> = {}) {
  const svgWithoutNamespaces = removeSafeNamespaceUris(svg, deps);
  if (
    (deps.unsafeSerializedContentPattern ?? UNSAFE_SERIALIZED_SVG_PATTERN).test(
      svgWithoutNamespaces,
    )
  ) {
    throw new Error(deps.unsafeSvgErrorMessage ?? UNSAFE_SVG_ERROR_MESSAGE);
  }
}

export function isUnsafeSvgElementName(name: string, deps: Partial<SvgSanitizerDeps> = {}) {
  const blockedActiveElementNames = deps.blockedActiveElementNames ?? BLOCKED_ACTIVE_ELEMENT_NAMES;
  return blockedActiveElementNames.has(getSvgLocalName(name));
}

export function isUnsafeSvgAttribute(
  name: string,
  value: unknown,
  deps: Partial<SvgSanitizerDeps> = {},
) {
  const normalizedName = name.toLowerCase();
  const normalizedLocalName = getSvgLocalName(normalizedName);
  const normalizedValue = normalizeSvgAttributeValue(value);

  if (isSvgNamespaceAttribute(normalizedName)) {
    return !(deps.safeNamespaceUris ?? SAFE_NAMESPACE_URIS).has(normalizedValue);
  }

  if (normalizedLocalName.startsWith('on')) {
    return true;
  }

  if (['href', 'src'].includes(normalizedLocalName)) {
    return !(deps.safeReferencePattern ?? SAFE_REFERENCE_PATTERN).test(normalizedValue);
  }

  return (deps.externalReferencePattern ?? EXTERNAL_REFERENCE_PATTERN).test(normalizedValue);
}

export function normalizeSvgAttributeValue(value: unknown) {
  const valueWithoutEscapedNewlines = String(value).replace(CSS_ESCAPED_NEWLINE_PATTERN, '');
  const decodedValue = decodeCssEscapes(stripUnsafeControlCharacters(valueWithoutEscapedNewlines));

  return stripUnsafeControlCharacters(decodedValue).trim();
}

export function getSvgLocalName(name: string) {
  const namespaceSeparatorIndex = name.toLowerCase().lastIndexOf(':');
  return namespaceSeparatorIndex === -1
    ? name.toLowerCase()
    : name.toLowerCase().slice(namespaceSeparatorIndex + 1);
}

export function isSvgNamespaceAttribute(name: string) {
  const normalizedName = name.toLowerCase();
  return normalizedName === 'xmlns' || normalizedName.startsWith('xmlns:');
}

function stripUnsafeControlCharacters(value: string) {
  let strippedValue = '';
  for (const character of String(value)) {
    const characterCode = character.charCodeAt(0);
    if (characterCode >= 32 && characterCode !== 127) {
      strippedValue += character;
    }
  }

  return strippedValue;
}

function decodeCssEscapes(value: string) {
  return value.replace(
    CSS_ESCAPE_PATTERN,
    (_match: string, hexEscape: string | undefined, simpleEscape: string | undefined) => {
      if (hexEscape) {
        return cssCodePointToCharacter(Number.parseInt(hexEscape, 16));
      }

      return simpleEscape ?? '';
    },
  );
}

function cssCodePointToCharacter(codePoint: number) {
  if (codePoint === 0 || codePoint > 0x10ffff || (codePoint >= 0xd800 && codePoint <= 0xdfff)) {
    return '\uFFFD';
  }

  return String.fromCodePoint(codePoint);
}

function removeSafeNamespaceUris(svg: string, deps: Partial<SvgSanitizerDeps>) {
  return [...(deps.safeNamespaceUris ?? SAFE_NAMESPACE_URIS)].reduce(
    (nextSvg, namespaceUri) => nextSvg.replaceAll(namespaceUri, ''),
    svg,
  );
}
